/**
 * SaveLoad.ts — Multi-slot persistence with pluggable storage backend for Gallowcrest.
 *
 * Supports 6 save slots (0 = auto-save, 1–5 = manual) plus a
 * separate vault for cross-playthrough data (achievements, completions).
 *
 * Default backend is localStorage (web). Desktop/Steam backends can
 * be swapped in via the StorageBackend interface.
 *
 * Includes one-time migration from legacy winterhold_* storage keys.
 */

import { GameState } from './GameState';
import type { SerializedGameState } from './GameState';

// ─── Save Schema ──────────────────────────────────────────────

export const SAVE_VERSION = 1;
export const MAX_SAVE_SLOTS = 6; // 0 = auto, 1–5 = manual

/** Metadata shown in the save slot browser (without loading full state). */
export interface SaveMetadata {
  saveSlot: number;
  savedAt: string;
  playtime: number;
  className: string;
  chapterNumber: number;
  chapterName: string;
  level: number;
  characterName: string;
}

/** Full save data stored per slot. */
export interface GallowcrestSaveData {
  version: number;
  saveSlot: number;
  savedAt: string;
  playtime: number;
  metadata: SaveMetadata;
  state: SerializedGameState;
}

// ─── Vault Schema ─────────────────────────────────────────────

export interface CompletionRecord {
  classId: string;
  endingId: string;
  completedAt: string;
  playtime: number;
  level: number;
}

export interface GallowcrestVault {
  version: number;
  unlockedAchievements: string[];
  completions: CompletionRecord[];
  totalPlaytime: number;
}

// ─── Storage Backend ──────────────────────────────────────────

/** Pluggable storage interface (localStorage, filesystem, Steam Cloud). */
export interface StorageBackend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Default localStorage backend for web. */
class LocalStorageBackend implements StorageBackend {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage full or unavailable
    }
  }
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable
    }
  }
}

// ─── Storage Keys ─────────────────────────────────────────────

function saveKey(slot: number): string {
  return `gallowcrest_save_${slot}`;
}

function legacySaveKey(slot: number): string {
  return `winterhold_save_${slot}`;
}

const VAULT_KEY = 'gallowcrest_vault';
const LEGACY_VAULT_KEY = 'winterhold_vault';
const LEGACY_SETTINGS_KEY = 'winterhold_settings';

// ─── SaveLoadSystem ───────────────────────────────────────────

export class SaveLoadSystem {
  private storage: StorageBackend;

  constructor(storage?: StorageBackend) {
    this.storage = storage ?? new LocalStorageBackend();
    this.migrateFromWinterhold();
  }

  /** One-time migration: copy old winterhold_* keys to gallowcrest_* if new don't exist. */
  private migrateFromWinterhold(): void {
    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      const oldKey = legacySaveKey(i);
      const newKey = saveKey(i);
      if (!this.storage.getItem(newKey) && this.storage.getItem(oldKey)) {
        this.storage.setItem(newKey, this.storage.getItem(oldKey)!);
      }
    }
    if (!this.storage.getItem(VAULT_KEY) && this.storage.getItem(LEGACY_VAULT_KEY)) {
      this.storage.setItem(VAULT_KEY, this.storage.getItem(LEGACY_VAULT_KEY)!);
    }
    if (!this.storage.getItem('gallowcrest_settings') && this.storage.getItem(LEGACY_SETTINGS_KEY)) {
      this.storage.setItem('gallowcrest_settings', this.storage.getItem(LEGACY_SETTINGS_KEY)!);
    }
  }

  // ─── Save ───────────────────────────────────────────────

  /** Save game state to a slot. Returns true if save succeeded and verified. */
  save(
    slot: number,
    state: GameState,
    chapterName: string = '',
    className: string = '',
  ): boolean {
    try {
      const metadata: SaveMetadata = {
        saveSlot: slot,
        savedAt: new Date().toISOString(),
        playtime: state.playtime,
        className: className || state.character.classId,
        chapterNumber: state.story.currentChapter,
        chapterName,
        level: state.character.level,
        characterName: state.character.name,
      };

      const saveData: GallowcrestSaveData = {
        version: SAVE_VERSION,
        saveSlot: slot,
        savedAt: metadata.savedAt,
        playtime: state.playtime,
        metadata,
        state: state.serialize(),
      };

      const serialized = JSON.stringify(saveData);
      this.storage.setItem(saveKey(slot), serialized);

      // Verify write succeeded by reading back
      const readBack = this.storage.getItem(saveKey(slot));
      if (!readBack || readBack !== serialized) {
        console.error(`[SaveSystem] Verification failed for slot ${slot}`);
        return false;
      }

      console.log(`[SaveSystem] Saved slot ${slot}: ${state.character.name} Ch.${state.story.currentChapter} (verified)`);
      return true;
    } catch (err) {
      console.error(`[SaveSystem] Save failed for slot ${slot}:`, err);
      return false;
    }
  }

  // ─── Load ───────────────────────────────────────────────

  /** Load game state from a slot. Returns null if slot is empty or corrupt. */
  load(slot: number): GameState | null {
    const raw = this.storage.getItem(saveKey(slot));
    if (!raw) return null;

    try {
      const saveData: GallowcrestSaveData = JSON.parse(raw);
      if (!saveData.state || typeof saveData.version !== 'number') return null;
      return GameState.deserialize(saveData.state);
    } catch {
      return null;
    }
  }

  // ─── Metadata ───────────────────────────────────────────

  /** Get save metadata for a slot without loading the full state. */
  getSaveMetadata(slot: number): SaveMetadata | null {
    const raw = this.storage.getItem(saveKey(slot));
    if (!raw) return null;

    try {
      const saveData: GallowcrestSaveData = JSON.parse(raw);
      return saveData.metadata ?? null;
    } catch {
      return null;
    }
  }

  /** Get metadata for all 6 save slots. */
  getAllSaveSlots(): (SaveMetadata | null)[] {
    const slots: (SaveMetadata | null)[] = [];
    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      slots.push(this.getSaveMetadata(i));
    }
    return slots;
  }

  /** Delete a save slot. */
  deleteSave(slot: number): void {
    this.storage.removeItem(saveKey(slot));
  }

  /** Get the most recent save across all slots (for "Continue" button). */
  getMostRecentSave(): { slot: number; metadata: SaveMetadata } | null {
    let best: { slot: number; metadata: SaveMetadata } | null = null;

    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      const meta = this.getSaveMetadata(i);
      if (!meta) continue;
      if (!best || meta.savedAt > best.metadata.savedAt) {
        best = { slot: i, metadata: meta };
      }
    }

    return best;
  }

  // ─── Vault ──────────────────────────────────────────────

  /** Load the persistent vault (achievements, completions). */
  loadVault(): GallowcrestVault {
    const raw = this.storage.getItem(VAULT_KEY);
    if (!raw) return this.createEmptyVault();

    try {
      const vault: GallowcrestVault = JSON.parse(raw);
      if (typeof vault.version !== 'number') return this.createEmptyVault();
      return vault;
    } catch {
      return this.createEmptyVault();
    }
  }

  /** Save the vault to storage. */
  saveVault(vault: GallowcrestVault): void {
    this.storage.setItem(VAULT_KEY, JSON.stringify(vault));
  }

  // ─── Export / Import ────────────────────────────────────

  /** Export a save slot as a JSON string (for manual backup). */
  exportSave(slot: number): string | null {
    return this.storage.getItem(saveKey(slot));
  }

  /** Import a save from a JSON string. Validates before storing. */
  importSave(json: string, targetSlot: number): boolean {
    try {
      const saveData: GallowcrestSaveData = JSON.parse(json);
      // Basic validation
      if (
        typeof saveData.version !== 'number' ||
        !saveData.state ||
        !saveData.metadata
      ) {
        return false;
      }
      // Verify it can be deserialized
      GameState.deserialize(saveData.state);
      // Update slot number
      saveData.saveSlot = targetSlot;
      saveData.metadata.saveSlot = targetSlot;
      this.storage.setItem(saveKey(targetSlot), JSON.stringify(saveData));
      return true;
    } catch {
      return false;
    }
  }

  // ─── Internal ───────────────────────────────────────────

  private createEmptyVault(): GallowcrestVault {
    return {
      version: 1,
      unlockedAchievements: [],
      completions: [],
      totalPlaytime: 0,
    };
  }
}
